import {
  destroy,
  detach,
  flow,
  getParentOfType,
  getRoot,
  getSnapshot,
  types,
} from 'mobx-state-tree';
import moment from 'moment';
import { RequestStatusMST } from 'store/models/requestsStore';
import { MessageSourceMST } from '../../constants';
import { MessageModel, MessagesParams } from '../message';
import { IConversationMember, IMessageModel } from '../../types';
import { RequestStateModel } from 'store/models/general-models';
import { FileModel } from 'store/models/files';
import { Messenger, Requests } from 'http-requests';
import { CreateMessageParams, MessageDTO } from 'http-requests/messenger';
import { IUserStore } from 'store/models/userStore';
import { MessengerStore } from '../../messenger';
import { formatAttachmentsCount } from '../../utils';

export const ConversationMember = types.model({
  id: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  full_name: types.optional(types.string, ''),
  provider_id: types.optional(types.string, ''),
  provider_name: types.optional(types.string, ''),
  position: types.optional(types.string, ''),
});

// /* Модель дополнительной информации по конкретному чату/даилогу */
// export const ConversationInfoModel = types.model('ConversationInfoModel', {
//   //TODO: это просто набросок, этого пока нет (вывод в правом инфоблоке сверху + возможно что-то ещё)
//   members: types.optional(types.array(MemberModel), []),
//   attachments: types.optional(types.array(types.string), []),
// });

/** Чат/Беседа/Диалог по конкретной заявке */
export const ConversationModel = types
  .model('ConversationModel', {
    conversation_id: types.optional(types.identifier, ''),
    request_id: types.optional(types.string, ''),
    request_number: types.optional(types.string, ''),
    request_address: types.optional(types.string, ''),
    request_status: types.optional(RequestStatusMST, 'accepted'),
    request_category: types.optional(types.string, ''),
    last_message_id: types.optional(types.string, ''),
    last_message_content: types.optional(types.string, ''),
    last_message_attached_files_ids: types.optional(
      types.array(types.string),
      []
    ),
    last_message_created_at: types.optional(types.string, ''),
    last_message_user_number: types.optional(
      types.maybeNull(types.string),
      null
    ),
    last_message_user_full_name: types.optional(types.string, ''),
    last_message_source: types.optional(MessageSourceMST, 'system'),
    last_message_is_removed: types.optional(types.boolean, false),

    messages: types.optional(types.array(MessageModel), []),
    messagesParams: types.optional(MessagesParams, {}),
    newMessageText: types.optional(types.string, ''),
    newAttachmentsIds: types.optional(types.array(types.string), []),
    replyMessage: types.optional(
      types.maybeNull(types.reference(MessageModel)),
      null
    ),
    request: types.optional(types.frozen<{ [key: string]: any }>(), {}),

    hasMessagesToUpload: types.optional(types.boolean, true),
    scrollAnchors: types.optional(types.array(types.string), []),
    last_message_read_users_numbers: types.optional(
      types.array(types.string),
      []
    ),
    unreadMessagesCount: types.optional(types.number, 0),
    isOverflown: types.optional(types.boolean, false),
    members: types.optional(types.array(ConversationMember), []),
    filesIds: types.optional(types.array(types.string), []),
    allFiles: types.optional(types.array(FileModel), []),
  })
  .volatile(() => ({
    getRequestStatus: RequestStateModel.create(),
    getMessagesStatuses: RequestStateModel.create(),
    createMessageStatuses: RequestStateModel.create(),
    getMembersStatus: RequestStateModel.create(),
    getFilesIdsStatus: RequestStateModel.create(),
    getAllFilesStatus: RequestStateModel.create(),
    deleteMessageStatus: RequestStateModel.create(),
    draftShown: false,
  }))
  .views((self) => ({
    get snapshot() {
      return getSnapshot(self);
    },
    get newMessages() {
      return self.messages.filter((message) => message.isNew);
    },
    get newMessagesCount(): number {
      return this.newMessages.length;
    },
    get hasNewMessageAnswer(): boolean {
      const user = getRoot<{ user: IUserStore }>(self).user;

      return this.newMessages.some((message) => {
        return (
          !!message.src?.user_number?.length &&
          message.src.user_number === user.auth.number
        );
      });
    },
    get lastMessageIsRead(): boolean {
      //TODO: проверить чтобы всегда актуальное было записано в last_message_read_users_numbers!
      return (
        !!self.last_message_read_users_numbers.length ||
        (!!self.messages[0] && !!self.messages[0].read.length)
      );
    },
    get firstNewMessageId(): string {
      const firstNewMessage = self.messages.filter((message, index) => {
        return (
          message.isNew &&
          (self.messages.length <= 1 ||
            (self.messages[index + 1] && !self.messages[index + 1].isNew))
        );
      });

      return firstNewMessage.length ? firstNewMessage[0].id : '';
    },
    get existingMessageIds() {
      return new Set(self.messages.map((m) => m.id));
    },
    get sender() {
      const user = getRoot<{ user: IUserStore }>(self).user;

      if (self.draftShown) {
        return {
          label: 'Черновик',
          value: self.newMessageText,
          labelColor: 'red.600' as const,
        };
      }

      return {
        label:
          user.auth.number === this.lastMessage.user_number
            ? 'Вы'
            : this.lastMessage.user_full_name,
        value: this.lastMessage.content,
        labelColor: 'gray.800' as const,
        isOwn: user.auth.number === this.lastMessage.user_number,
        is_removed: this.lastMessage.is_removed,
      };
    },
    get sendDisabled(): boolean {
      return !self.newMessageText.trim().length;
    },
    get isArchive() {
      return ['performed', 'abandonment', 'refusal'].includes(
        self.request_status
      );
    },
    get lastMessage() {
      const id = self.last_message_id;
      const created_at = self.last_message_created_at;
      const user_number = self.last_message_user_number;
      const user_full_name = self.last_message_user_full_name;

      const content = self.last_message_content;
      const attachedIds = self.last_message_attached_files_ids ?? [];
      const is_removed = self.last_message_is_removed;

      const finalContent =
        (!content || content.trim() === '') && attachedIds.length > 0
          ? formatAttachmentsCount(attachedIds.length)
          : content;

      return {
        id,
        content: finalContent,
        created_at,
        user_number,
        user_full_name,
        is_removed,
      };
    },
    get requestData() {
      return {
        id: self.request_id,
        number: self.request_number,
        address: self.request_address,
        status: self.request_status,
        category: self.request_category,
      };
    },
    getMessageById(id: string): IMessageModel | null {
      return self.messages.find((message) => message.id === id) || null;
    },
    /* Поиск сообщения в ответе */
    getMessageWithAnswerById(id: string): IMessageModel | null {
      return self.messages.find((message) => message.src?.id === id) || null;
    },
  }))
  .actions((self) => ({
    setFilesIds(ids: string[]) {
      self.filesIds.replace([...self.filesIds, ...ids]);
    },
    removeFileId(fileId: string) {
      self.filesIds.replace(self.filesIds.filter((id) => id !== fileId));
      self.allFiles.replace(self.allFiles.filter((file) => file.id !== fileId));
    },
    setUnreadMessagesCount(data: number) {
      self.unreadMessagesCount = data;
    },
    changeLastMessageData() {
      const lastMessage = self.messages?.[0];

      if (!lastMessage) {
        return;
      }

      self.last_message_read_users_numbers.replace(
        lastMessage.read.map((item) => item.number)
      );
      self.last_message_user_number = lastMessage.user_number;
      self.last_message_id = lastMessage.id;
      self.last_message_content = lastMessage.content;
      self.last_message_created_at = lastMessage.created_at;
      self.last_message_user_full_name = lastMessage.user_full_name;
      self.last_message_is_removed = lastMessage.is_removed;
    },
    makeLastMessageRemoved() {
      self.last_message_content = 'Данное сообщение удалено';
      self.last_message_is_removed = true;
    },
    changeLastMessageReadData(userNumber: string) {
      self.last_message_read_users_numbers.replace([
        ...self.last_message_read_users_numbers,
        userNumber,
      ]);
    },
    addMessage(
      incoming: MessageDTO,
      replyMessage?: Omit<MessageDTO, 'src'> | null
    ) {
      const messengerStore = getParentOfType(self, MessengerStore);
      const temp = self.messages.find(
        (message) =>
          message.id.startsWith('temp-') &&
          message.content === incoming.content &&
          Math.abs(
            moment(message.created_at).diff(
              moment.utc(incoming.created_at),
              'second'
            )
          ) < 5
      );

      if (temp) {
        const index = self.messages.indexOf(temp);
        detach(temp);
        self.messages.splice(
          index,
          0,
          MessageModel.create({
            ...incoming,
            id: String(incoming.id),
            conversation_id: String(incoming.conversation_id),
            src: incoming.src ?? replyMessage ?? null,
            is_removed: false,
          })
        );
        messengerStore.moveConversationToTop(incoming.conversation_id);
        this.changeLastMessageData();

        return;
      }

      if (!self.existingMessageIds.has(String(incoming.id))) {
        self.messages.unshift(
          MessageModel.create({
            ...incoming,
            id: String(incoming.id),
            conversation_id: String(incoming.conversation_id),
            src: incoming.src ?? replyMessage ?? null,
            is_removed: false,
          })
        );
        messengerStore.moveConversationToTop(incoming.conversation_id);
        this.changeLastMessageData();
      }
    },
    setMessages(newMessages: IMessageModel[]) {
      self.messages.replace(self.messages.concat(self.messages, newMessages));
    },
    setNewMessageText(text: string) {
      self.newMessageText = text;
    },
    setNewAttachments(value: string[]) {
      self.newAttachmentsIds.replace(value);
    },
    setReplyMessage(message: IMessageModel | null) {
      self.replyMessage = message;
    },
    setDraftShown(value: boolean) {
      if (self.draftShown !== value) {
        self.draftShown = value;
      }
    },
    pushScrollAnchor(messageId: string) {
      if (!self.scrollAnchors.includes(messageId)) {
        self.scrollAnchors.push(messageId);
      }
    },
    popScrollAnchor(): string | undefined {
      return self.scrollAnchors.pop();
    },
    clearScrollAnchors() {
      self.scrollAnchors.clear();
    },
    clearMessages() {
      self.messages.clear();
      self.unreadMessagesCount = 0;
      self.messagesParams.clearOffset();
    },
    addMember(member: IConversationMember) {
      self.members.replace([...self.members, member]);
    },
  }))
  .actions((self) => ({
    getMessages: flow(function* (conversationID?: string) {
      try {
        self.getMessagesStatuses.loadingStates();

        const response: MessageDTO[] = yield Messenger.GetMessages(
          conversationID || self.conversation_id,
          self.messagesParams
        );

        const newMessages = response
          .filter((m) => !self.existingMessageIds.has(m.id))
          .map((m) => MessageModel.create(m));

        self.messages.replace([...self.messages, ...newMessages]);
        self.hasMessagesToUpload =
          response.length === self.messagesParams.limit;
        self.getMessagesStatuses.successStates();

        return self.messages;
      } catch (e) {
        console.error(e);
        self.getMessagesStatuses.errorStates();
      } finally {
        self.getMessagesStatuses.finallyStates();
      }
    }),
    getPinnedMessages: flow(function* () {
      // TODO: запрос на список закрепленных сообщений в чате
    }),
    uploadFiles: flow(function* (formData: FormData) {
      try {
        const response: { id: string } = yield Messenger.UploadFiles(formData);

        return response;
      } catch (error) {
        console.error(error);

        return Promise.reject(error);
      }
    }),
    sendMessage: flow(function* (data: CreateMessageParams) {
      const user = getRoot<{ user: IUserStore }>(self).user;
      const tempID = `temp-${Date.now()}`;
      const tempMessage = MessageModel.create({
        ...data,
        user_number: user.auth.number,
        id: tempID,
        source: 'user',
        created_at: moment().toISOString(),
        src: self.replyMessage ? self.replyMessage.snapshot : null,
      });

      self.messages.unshift(tempMessage);
      self.changeLastMessageData();

      const newMessage = self.newMessageText;
      const replyMessage = self.replyMessage;

      try {
        tempMessage.createStatus.loadingStates();

        self.setNewMessageText('');
        self.setReplyMessage(null);

        const response = yield Messenger.CreateMessage(self.conversation_id, {
          ...data,
          ...(replyMessage
            ? { reply_to_id: Number(replyMessage.id) }
            : undefined),
        });
        self.addMessage(response, replyMessage?.snapshot);

        tempMessage.createStatus.successStates();
      } catch (error: any) {
        destroy(tempMessage);
        self.setNewMessageText(newMessage);
        self.setReplyMessage(replyMessage);

        console.error(error);

        if (error?.detail?.includes('Нарушен лимит')) {
          self.isOverflown = true;
        }

        tempMessage.createStatus.errorStates();

        return Promise.reject(error);
      } finally {
        tempMessage.createStatus.finallyStates();
      }
    }),
    getRequest: flow(function* () {
      try {
        self.getRequestStatus.loadingStates();
        const response = yield Requests.GetRequest(self.request_id);
        self.getRequestStatus.successStates();
        self.request = response;

        return self.request;
      } catch (e) {
        console.error(e);
        self.getRequestStatus.errorStates();
      } finally {
        self.getRequestStatus.finallyStates();
      }
    }),
    getMembers: flow(function* () {
      try {
        self.getMembersStatus.loadingStates();

        const response = yield Messenger.GetConversationUsers(
          self.conversation_id
        );
        self.members.replace(response);

        self.getMembersStatus.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.getMembersStatus.errorStates();
      } finally {
        self.getMembersStatus.finallyStates();
      }
    }),
    getFilesIds: flow(function* () {
      try {
        self.getFilesIdsStatus.loadingStates();

        const response: string[] = yield Messenger.GetConversationFiles(
          self.conversation_id
        );
        self.filesIds.replace(response);

        self.getFilesIdsStatus.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.getFilesIdsStatus.errorStates();
      } finally {
        self.getFilesIdsStatus.finallyStates();
      }
    }),
    getAllFiles: flow(function* () {
      try {
        if (!self.filesIds.length) {
          return;
        }

        self.getAllFilesStatus.loadingStates();

        const map = new Map<string, string>();
        self.allFiles.replace(
          self.filesIds.map((id) => FileModel.create({ id }))
        );

        const sharedFilesIds = yield Messenger.GetFilesSharedIds(self.filesIds);

        sharedFilesIds.forEach(
          (item: { file_id: string; shared_id: string }) => {
            map.set(item.file_id, item.shared_id);
          }
        );

        self.allFiles.forEach(async (file) => {
          const sharedId = map.get(file.id);

          if (sharedId) {
            file.updateSharedId(sharedId);
          }

          if (!file.filename) {
            await file.getInfo();
          }
        });

        self.getAllFilesStatus.successStates();

        return self.allFiles;
      } catch (e) {
        console.error(e);
        self.getAllFilesStatus.errorStates();
      } finally {
        self.getAllFilesStatus.finallyStates();
      }
    }),
    deleteMessage: flow(function* (messageId: string) {
      try {
        self.deleteMessageStatus.loadingStates();

        const response = yield Messenger.DeleteMessage(
          self.conversation_id,
          messageId
        );

        const initialMessage = self.getMessageById(messageId);
        const initialAnswer = self.getMessageWithAnswerById(messageId);

        if (initialMessage) {
          initialMessage.setMessageDelete(response.content);
        }

        if (initialAnswer) {
          initialAnswer.src?.setMessageDelete(response.content);
        }

        self.deleteMessageStatus.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.deleteMessageStatus.errorStates();

        throw e;
      } finally {
        self.deleteMessageStatus.finallyStates();
      }
    }),
  }));
